import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ExistingClientIcon from "@mui/icons-material/HowToRegRounded";
import MergeIcon from "@mui/icons-material/MergeRounded";
import NewRegistrationIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import SubmittedIcon from "@mui/icons-material/PublicRounded";
import { Box, Button, Chip, Divider, Paper, Stack, Tooltip, Typography, alpha } from "@mui/material";
import { formatShortDate, formatTime } from "@shared";

import { NotImplementedWrapper } from "@/common/components/not-implemented-wrapper/NotImplementedWrapper";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { InternetRegistrationPartyCard } from "../party-card/InternetRegistrationPartyCard";
import { InternetRegistrationEntryProps } from "./InternetRegistrationEntry.types";

export function InternetRegistrationEntry({ entry, isDeciding, onNotADuplicate }: InternetRegistrationEntryProps) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();

  const { matchingFields, duplicateCandidate } = entry;
  const matchChips = [
    { key: "lastName", isMatch: matchingFields?.lastName, label: t("internetRegistrations.matching.lastName") },
    { key: "firstName", isMatch: matchingFields?.firstName, label: t("internetRegistrations.matching.firstName") },
    { key: "birthday", isMatch: matchingFields?.birthday, label: t("internetRegistrations.matching.birthday") },
  ].filter((chip) => chip.isMatch);

  return (
    <Paper variant="outlined" component="section">
      <Stack direction="row" alignItems="center" gap={1} px={2} py={1} sx={{ bgcolor: "action.hover" }}>
        <SubmittedIcon fontSize="small" sx={{ color: "primary.main" }} />
        <Typography variant="body2" color="text.primary">
          {t("internetRegistrations.entryHeaderLabel")}
          {" · "}
          <Box component="span" fontWeight={700}>
            {t("internetRegistrations.entryHeaderDateTime", {
              date: formatShortDate(entry.submittedUtc, locales[language]),
              time: formatTime(entry.submittedUtc, locales[language]),
            })}
          </Box>
        </Typography>
      </Stack>

      <Divider />

      <Stack
        direction={{ xs: "column", md: "row" }}
        p={2}
        gap={2}
        divider={<Divider flexItem orientation="vertical" />}
      >
        <InternetRegistrationPartyCard
          label={t("internetRegistrations.newRegistration")}
          icon={<NewRegistrationIcon fontSize="small" />}
          tone="new"
          party={entry.newRegistration}
        />
        {/* The compared file can be gone. The entry stays so the registration can still be decided —
            without it the file would keep its IA status with nothing able to clear it. */}
        {duplicateCandidate ? (
          <InternetRegistrationPartyCard
            label={t("internetRegistrations.duplicateCandidate")}
            icon={<ExistingClientIcon fontSize="small" />}
            tone="existing"
            party={duplicateCandidate}
          />
        ) : (
          <Stack
            component="section"
            aria-label={t("internetRegistrations.duplicateCandidate")}
            flex={1}
            minWidth={0}
            justifyContent="center"
          >
            <Typography variant="body2" color="text.secondary">
              {t("internetRegistrations.duplicateCandidateGone")}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Divider />

      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap={2}
        px={2}
        py={1.5}
      >
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          {/* Nothing to compare against means no verdict to report, not "nothing matched". */}
          {matchingFields && (
            <Typography variant="body2" color="text.primary" fontWeight={700}>
              {t("internetRegistrations.matching.label")}
            </Typography>
          )}
          {matchChips.map((chip) => (
            <Chip
              key={chip.key}
              size="small"
              variant="outlined"
              icon={<CheckRoundedIcon />}
              label={chip.label}
              // These are read-only markers, not filters: the default text caret would invite a click.
              sx={{
                cursor: "default",
                color: "success.main",
                borderColor: (theme) => alpha(theme.palette.success.main, 0.5),
                bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
                "& .MuiChip-icon": { color: "success.main" },
              }}
            />
          ))}
        </Stack>

        <Box display="flex" gap={1} justifyContent="flex-end">
          <Button
            variant="outlined"
            disabled={isDeciding}
            onClick={() => onNotADuplicate(entry.newRegistration.clientId)}
          >
            {t("internetRegistrations.actions.notADuplicate")}
          </Button>
          {/* The Kund:innen-Fusionierung is its own story; this is only its entry point. With the
              compared file gone there is nothing to merge into — aria-disabled rather than disabled so
              the tooltip saying why stays reachable by keyboard. */}
          <Tooltip title={duplicateCandidate ? "" : t("internetRegistrations.mergeUnavailable")}>
            <span>
              <NotImplementedWrapper>
                <Button
                  variant="contained"
                  startIcon={<MergeIcon />}
                  disabled={isDeciding}
                  aria-disabled={!duplicateCandidate}
                  sx={duplicateCandidate ? undefined : { opacity: 0.5, cursor: "not-allowed" }}
                >
                  {t("internetRegistrations.actions.merge")}
                </Button>
              </NotImplementedWrapper>
            </span>
          </Tooltip>
        </Box>
      </Stack>
    </Paper>
  );
}
